<template>
  <nav
    class="navbar navbar-expand-lg navbar-transparent navbar-absolute app-navbar"
    :class="{ 'rtl-navbar': locale === 'ar' }"
  >
    <div class="container-fluid app-navbar__container">
      <div class="app-navbar__spacer" :class="{ 'is-ltr': locale !== 'ar' }"></div>

      <div class="app-navbar__items">
        <ul class="navbar-nav app-navbar__nav">
          <li class="nav-item">
            <LanguageSwitcher />
          </li>

          <!-- Super Admin School Button -->
          <li v-if="isSuperAdmin" class="nav-item">
            <button
              type="button"
              class="school-trigger"
              :class="{ 'is-open': showSchools }"
              @click="toggleSchools"
            >
              <i class="fas fa-school"></i>
              <span class="school-trigger__text">
                {{ currentTenant?.name || schoolText.selectSchool }}
              </span>
            </button>
          </li>

          <!-- Profile -->
          <li class="nav-item app-navbar__dropdown">
            <button
              type="button"
              class="nav-link profile-trigger"
              :class="{ 'is-open': showProfile }"
              @click="toggleProfile"
              id="navbarDropdownProfile"
              aria-haspopup="true"
              :aria-expanded="showProfile ? 'true' : 'false'"
            >
              <img
                :src="getAvatarUrl"
                class="navbar-avatar rounded-circle"
                :alt="$t('navbar.profile')"
                @error="handleImageError"
              />
              <span class="navbar-user-name d-none d-md-inline">
                {{ userName }}
              </span>
            </button>

            <div
              class="app-dropdown"
              :class="{ show: showProfile }"
              aria-labelledby="navbarDropdownProfile"
              role="menu"
            >
              <router-link class="app-dropdown__item" :to="{ name: 'profile' }" @click="closeProfile">
                <i class="fas fa-id-card"></i>
                <span>{{ $t("navbar.profile") }}</span>
              </router-link>

              <router-link
                v-if="showDashboardLink"
                class="app-dropdown__item"
                :to="dashboardRoute"
                @click="closeProfile"
              >
                <i class="fas fa-tachometer-alt"></i>
                <span>{{ $t("navbar.dashboard") }}</span>
              </router-link>

              <div class="app-dropdown__divider"></div>

              <button class="app-dropdown__item" type="button" @click="logout">
                <i class="fas fa-sign-out-alt"></i>
                <span>{{ $t("navbar.logout") }}</span>
              </button>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </nav>

  <!-- Schools Side Drawer -->
  <teleport to="body">
    <div
      v-if="isSuperAdmin"
      class="school-drawer-overlay"
      :class="{ show: showSchools }"
      @click="closeSchools"
    ></div>

    <aside
      v-if="isSuperAdmin"
      class="school-drawer"
      :class="[
        { show: showSchools },
        locale === 'ar' ? 'is-rtl' : 'is-ltr'
      ]"
      :dir="locale === 'ar' ? 'rtl' : 'ltr'"
    >
      <div class="school-drawer__header">
        <div>
          <h6>{{ schoolText.schools }}</h6>
          <small>{{ schoolText.subtitle }}</small>
        </div>

        <button type="button" class="school-drawer__close" @click="closeSchools">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="school-drawer__list">
        <button
          v-for="school in tenants"
          :key="school.id"
          type="button"
          class="school-drawer__item"
          :class="{ active: currentTenant?.id === school.id }"
          @click="selectSchool(school)"
        >
          <div class="school-drawer__icon">
            <i class="fas fa-school"></i>
          </div>

          <div class="school-drawer__info">
            <strong>{{ school.name }}</strong>
            <small>{{ school.slug }}</small>
          </div>

          <i
            v-if="currentTenant?.id === school.id"
            class="fas fa-check school-drawer__check"
          ></i>
        </button>

        <div v-if="!tenants.length" class="school-drawer__empty">
          {{ schoolText.noSchools }}
        </div>
      </div>
    </aside>
  </teleport>
</template>

<script setup>
import { ref, computed, onMounted, watch, onUnmounted, defineEmits } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import Cookies from "js-cookie";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher.vue";
import api from "@/api";

const emit = defineEmits(["logout"]);
const { locale } = useI18n();
const store = useStore();

const teacherDetails = ref(null);
const studentDetails = ref(null);
const guardianDetails = ref(null);
const showProfile = ref(false);
const showSchools = ref(false);

const currentUser = computed(() => {
  if (store.state.user) return store.state.user;
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch {
    return null;
  }
});

const userRoleSlug = computed(() => currentUser.value?.role?.slug || Cookies.get("user_type") || "");
const isSuperAdmin = computed(() => userRoleSlug.value === "super_admin");
const isTeacher = computed(() => userRoleSlug.value === "teacher");
const isStudent = computed(() => userRoleSlug.value === "student");
const isGuardian = computed(() => userRoleSlug.value === "guardian");

const tenants = computed(() => store.getters.tenants || []);
const currentTenant = computed(() => store.getters.currentTenant);

const schoolText = computed(() => {
  if (locale.value === "ar") {
    return {
      schools: "المدارس",
      subtitle: "اختار مدرسة لعرض بياناتها",
      selectSchool: "اختيار مدرسة",
      noSchools: "لا توجد مدارس",
    };
  }

  return {
    schools: "Schools",
    subtitle: "Select a school to view its data",
    selectSchool: "Select School",
    noSchools: "No schools found",
  };
});

const toggleProfile = () => {
  showProfile.value = !showProfile.value;
  showSchools.value = false;
};

const closeProfile = () => {
  showProfile.value = false;
};

const toggleSchools = () => {
  showSchools.value = !showSchools.value;
  showProfile.value = false;
};

const closeSchools = () => {
  showSchools.value = false;
};

const selectSchool = async (school) => {
  if (currentTenant.value?.id === school.id) {
    closeSchools();
    return;
  }

  await store.dispatch("switchTenant", school);
  closeSchools();

  window.location.reload();
};

const getBaseUrl = () => {
  try {
    if (import.meta.env && import.meta.env.VITE_API_BASE_URL) {
      return import.meta.env.VITE_API_BASE_URL;
    }
  } catch (e) {
    console.warn(e);
  }
  return window.location.origin;
};

const fetchTeacherDetails = async (teacherId) => {
  try {
    const response = await api.get(`/teachers/${teacherId}`);
    teacherDetails.value = response.data.data;
  } catch (error) {
    console.error("Error fetching teacher details:", error);
  }
};

const fetchStudentDetails = async (studentId) => {
  try {
    const response = await api.get(`/students/${studentId}`);
    studentDetails.value = response.data.data;
  } catch (error) {
    console.error("Error fetching student details:", error);
  }
};

const fetchGuardianDetails = async (guardianId) => {
  try {
    const response = await api.get(`/guardians/${guardianId}`);
    guardianDetails.value = response.data.data;
  } catch (error) {
    console.error("Error fetching guardian details:", error);
  }
};

watch(
  currentUser,
  async (newUser) => {
    if (newUser?.id) {
      if (isTeacher.value) await fetchTeacherDetails(newUser.id);
      else if (isStudent.value) await fetchStudentDetails(newUser.id);
      else if (isGuardian.value) await fetchGuardianDetails(newUser.id);
    }
  },
  { immediate: true }
);

const getAvatarUrl = computed(() => {
  const user = currentUser.value;
  if (!user) return "/images/default-avatar.png";

  const baseUrl = getBaseUrl();

  if (isTeacher.value && teacherDetails.value) {
    if (teacherDetails.value.image_url) {
      return teacherDetails.value.image_url.startsWith("http")
        ? teacherDetails.value.image_url
        : `${baseUrl}${teacherDetails.value.image_url}`;
    }
    if (teacherDetails.value.image) {
      const imagePath = teacherDetails.value.image.replace(/^\/+/, "");
      return imagePath.startsWith("storage/")
        ? `${baseUrl}/${imagePath}`
        : `${baseUrl}/storage/${imagePath}`;
    }
  }

  if (isStudent.value && studentDetails.value) {
    if (studentDetails.value.image_url) {
      return studentDetails.value.image_url.startsWith("http")
        ? studentDetails.value.image_url
        : `${baseUrl}${studentDetails.value.image_url}`;
    }
    if (studentDetails.value.image) {
      const imagePath = studentDetails.value.image.replace(/^\/+/, "");
      return imagePath.startsWith("storage/")
        ? `${baseUrl}/${imagePath}`
        : `${baseUrl}/storage/${imagePath}`;
    }
  }

  if (isGuardian.value && guardianDetails.value) {
    if (guardianDetails.value.avatar_url) {
      return guardianDetails.value.avatar_url.startsWith("http")
        ? guardianDetails.value.avatar_url
        : `${baseUrl}${guardianDetails.value.avatar_url}`;
    }
    if (guardianDetails.value.avatar) {
      const avatarPath = guardianDetails.value.avatar.replace(/^\/+/, "");
      return avatarPath.startsWith("storage/")
        ? `${baseUrl}/${avatarPath}`
        : `${baseUrl}/storage/${avatarPath}`;
    }
  }

  if (user.avatar_url) {
    return user.avatar_url.startsWith("http") ? user.avatar_url : `${baseUrl}${user.avatar_url}`;
  }

  if (user.avatar) {
    const avatarPath = user.avatar.replace(/^\/+/, "");
    return avatarPath.startsWith("storage/")
      ? `${baseUrl}/${avatarPath}`
      : `${baseUrl}/storage/${avatarPath}`;
  }

  if (user.image_url) {
    return user.image_url.startsWith("http") ? user.image_url : `${baseUrl}${user.image_url}`;
  }

  if (user.image) {
    const imagePath = user.image.replace(/^\/+/, "");
    return imagePath.startsWith("storage/")
      ? `${baseUrl}/${imagePath}`
      : `${baseUrl}/storage/${imagePath}`;
  }

  return "/images/default-avatar.png";
});

const userName = computed(() => {
  const u = currentUser.value;

  if (u) {
    if (isTeacher.value && teacherDetails.value) {
      return teacherDetails.value.username || u.username || u.name || "";
    }

    if (isStudent.value && studentDetails.value) {
      if (studentDetails.value.full_name) return studentDetails.value.full_name;
      const fullName = `${studentDetails.value.first_name || ""} ${studentDetails.value.father_name || ""} ${studentDetails.value.last_name || ""}`.trim();
      if (fullName) return fullName;
    }

    if (isGuardian.value && guardianDetails.value) {
      return guardianDetails.value.name || u.name || u.username || "";
    }

    if (u.name) return u.name;
    if (u.username) return u.username;
  }

  const cookieName = Cookies.get("user_name");
  if (cookieName) return decodeURIComponent(cookieName);

  return locale.value === "ar" ? "مستخدم" : "User";
});

const handleImageError = (e) => {
  e.target.src = "/images/default-avatar.png";
};

const closeOnOutside = (e) => {
  const profileBtn = document.getElementById("navbarDropdownProfile");
  const profileMenu = document.querySelector(".app-dropdown");

  if (
    showProfile.value &&
    profileBtn &&
    !profileBtn.contains(e.target) &&
    profileMenu &&
    !profileMenu.contains(e.target)
  ) {
    closeProfile();
  }
};

const closeOnEsc = (e) => {
  if (e.key === "Escape") {
    closeProfile();
    closeSchools();
  }
};

const showDashboardLink = computed(() => !["finance", "canteen"].includes(userRoleSlug.value));

const dashboardRoute = computed(() => {
  if (userRoleSlug.value === "school_admin") return "/admin/dashboard";
  if (userRoleSlug.value === "academic_manager") return "/academic/dashboard";
  if (userRoleSlug.value === "finance") return "/finance/dashboard";
  if (userRoleSlug.value === "student_affairs") return "/student-affairs/dashboard";
  if (userRoleSlug.value === "teacher") return "/teacher/dashboard";
  if (userRoleSlug.value === "student") return "/student/dashboard";
  if (userRoleSlug.value === "guardian") return "/guardian/dashboard";
  if (userRoleSlug.value === "canteen") return "/canteen/dashboard";
  return "/unauthorized";
});

const logout = () => {
  closeProfile();
  closeSchools();
  emit("logout");
};

onMounted(async () => {
  document.addEventListener("pointerdown", closeOnOutside);
  document.addEventListener("keydown", closeOnEsc);

  if (isSuperAdmin.value) {
    const schools = await store.dispatch("fetchTenants");

    if (!store.getters.currentTenant && schools.length > 0) {
      await store.dispatch("switchTenant", schools[0]);
    }
  }
});

onUnmounted(() => {
  document.removeEventListener("pointerdown", closeOnOutside);
  document.removeEventListener("keydown", closeOnEsc);
});
</script>

<style scoped>
.app-navbar {
  padding: 0.5rem 0.75rem;
  min-height: 56px;
  z-index: 900;
  backdrop-filter: blur(10px);
}

.app-navbar__container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.app-navbar__spacer {
  flex: 1 1 auto;
}

@media (min-width: 992px) {
  .app-navbar__spacer.is-ltr {
    margin-inline-start: 280px;
  }
}

.app-navbar__items {
  flex: 0 0 auto;
}

.app-navbar__nav {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
}

.app-navbar__dropdown {
  position: relative;
}

.profile-trigger {
  border: 0;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  color: #3c4858;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.profile-trigger i {
  font-size: 1.65rem;
  line-height: 1;
}

.profile-trigger:hover,
.profile-trigger.is-open {
  color: #2c91ae;
  background: rgba(44, 145, 174, 0.08);
}
.school-trigger {
  border: 0;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  color: #3c4858;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.school-trigger:hover,
.school-trigger.is-open {
  color: #2c91ae;
  background: rgba(44, 145, 174, 0.08);
}

.school-trigger i {
  font-size: 1.3rem;
  line-height: 1;
}

.school-trigger__text {
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.navbar-user-name {
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-dropdown {
  display: none;
  position: absolute;
  top: calc(100% + 10px);
  inset-inline-end: 0;
  inset-inline-start: auto;
  width: 230px;
  max-width: 92vw;
  padding: 0.35rem 0;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
  border-radius: 12px;
  overflow: hidden;
  z-index: 1200;
}

.app-dropdown.show {
  display: block;
}

.app-dropdown__item {
  width: 100%;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 1rem;
  color: #333;
  font-size: 0.95rem;
  text-align: start;
  text-decoration: none;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.app-dropdown__item i {
  width: 18px;
  text-align: center;
}

.app-dropdown__item:hover {
  background: #f8f9fa;
  color: #2c91ae;
}

.app-dropdown__divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 0.25rem 0;
}

.navbar-avatar {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border: 2px solid #f0f0f0;
}

.school-drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.25);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  z-index: 1300;
  transition: opacity 0.25s ease, visibility 0.25s ease;
}

.school-drawer-overlay.show {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.school-drawer {
  position: fixed;
  top: 0;
  bottom: 0;
  width: 320px;
  max-width: 88vw;
  background: #fff;
  z-index: 1400;
  padding: 18px;
  overflow-y: auto;
  transition: transform 0.28s ease;
  will-change: transform;
}

.school-drawer.is-ltr {
  right: 0;
  left: auto;
  transform: translateX(105%);
  box-shadow: -12px 0 35px rgba(0, 0, 0, 0.16);
}

.school-drawer.is-rtl {
  left: 0;
  right: auto;
  transform: translateX(-105%);
  box-shadow: 12px 0 35px rgba(0, 0, 0, 0.16);
}

.school-drawer.show {
  transform: translateX(0);
}

.school-drawer__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.school-drawer__header h6 {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: #344767;
}

.school-drawer__header small {
  color: #8392ab;
  font-size: 0.78rem;
}

.school-drawer__close {
  border: 0;
  background: #f5f7fb;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #67748e;
  cursor: pointer;
  flex-shrink: 0;
}

.school-drawer__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.school-drawer__item {
  width: 100%;
  border: 1px solid #edf0f5;
  background: #fff;
  border-radius: 16px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: start;
  cursor: pointer;
  transition: 0.2s ease;
}

.school-drawer__item:hover {
  background: #f8fbff;
  border-color: rgba(44, 145, 174, 0.25);
}

.school-drawer__item.active {
  background: rgba(44, 145, 174, 0.09);
  border-color: rgba(44, 145, 174, 0.35);
}

.school-drawer__icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2c91ae;
  flex-shrink: 0;
}

.school-drawer__info {
  flex: 1;
  min-width: 0;
}

.school-drawer__info strong {
  display: block;
  color: #344767;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.school-drawer__info small {
  display: block;
  color: #8392ab;
  font-size: 0.75rem;
}

.school-drawer__check {
  color: #2c91ae;
}

.school-drawer__empty {
  color: #8392ab;
  font-size: 0.9rem;
  text-align: center;
  padding: 1rem;
}

:deep(.language-switcher-container) {
  position: relative;
  margin-left: 0;
}

:deep(.language-switcher-btn) {
  border: 0;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.6rem;
  border-radius: 999px;
  color: #3c4858;
  transition: background-color 0.2s ease, color 0.2s ease;
}

:deep(.language-switcher-btn:hover) {
  color: #2c91ae;
  background: rgba(44, 145, 174, 0.08);
}

:deep(.language-menu) {
  position: absolute;
  top: calc(100% + 10px);
  inset-inline-end: 0;
  inset-inline-start: auto;
  width: 200px;
  max-width: 92vw;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
  z-index: 1200;
}

:deep(.language-menu button) {
  width: 100%;
  padding: 0.65rem 1rem;
  text-align: start;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  transition: background-color 0.15s ease, color 0.15s ease;
}

:deep(.language-menu button:hover),
:deep(.language-menu button:focus) {
  background: #f8f9fa;
  outline: none;
}

:deep(.language-menu button.active) {
  color: #2c91ae;
  font-weight: 700;
}

:deep(.current-language) {
  font-weight: 600;
}

@media (max-width: 767.98px) {
  :deep(.current-language),
 .school-trigger__text {
  display: none;
}

.school-trigger {
  width: 34px;
  height: 34px;
  justify-content: center;
  padding: 0;
}
}
</style>